import {IPortalseite} from "./Interfaces";
import Veraportal from "./Veraportal";
import Modal from "./Modal";
import VeraApi from "./VeraApi";
import Utilities from "./Utilities";

/**
 * Repräsentiert die Seite zur Verwaltung des schulweiten Testmodus oder der Testheftvariante.
 * Erweitert die Basisfunktionalität von Veraportal und implementiert IPortalseite.
 */
export default class SchuleVerwalten extends Veraportal implements IPortalseite {
    /**
     * Wird beim Laden der Seite asynchron ausgeführt.
     * Initialisiert die UI-Elemente und Event-Listener für die schulweite Testentscheidung.
     * @returns {Promise<boolean>} Ein Promise, das true zurückgibt, wenn die Seite erfolgreich geladen wurde.
     */
    override async onSeiteLoad(): Promise<boolean> {
        if (this.sessionvariablen.projekt.weiche === 'vera8') {
            const testmodusSelect = document.querySelector<HTMLSelectElement>('#testmodus-select');
            const submitButton = document.querySelector<HTMLButtonElement>('#testmodus-speichern');
            const ppBegruendung = document.querySelector<HTMLTextAreaElement>('#pp-begruendung');
            const ppBegruendungZeichen = document.querySelector<HTMLElement>('#pp-begruendung-zeichen');
            const isPpSchule = [this.schulstatus.is_cbt_schule_mathematik, this.schulstatus.is_cbt_schule_deutsch, this.schulstatus.is_cbt_schule_englisch, this.schulstatus.is_cbt_schule_franzoesisch].every(modus => modus === 0);
            if (testmodusSelect === null || submitButton === null)
                return false;

            // Setzt den initialen Wert des Testmodus-Dropdowns basierend auf dem Schulstatus
            if (this.schulstatus.is_cbt_schule_mathematik === 1)
                testmodusSelect.value = 'cbt';
            else if (this.schulstatus.is_cbt_schule_mathematik === 0)
                testmodusSelect.value = 'pp';
            else
                testmodusSelect.value = '-';

            if (ppBegruendung !== null)
                ppBegruendung.value = this.schulstatus.pp_begruendung ?? '';

            const aktualisiereTestmodus = (): void => {
                const isPp = testmodusSelect.value === 'pp';
                submitButton.disabled = !['pp', 'cbt'].includes(testmodusSelect.value);
                if (ppBegruendung === null || ppBegruendungZeichen === null)
                    return;

                // Dieselbe Bereinigung wie in testmodus-aendern.php; Randabstände erst beim Verlassen entfernen
                const bereinigt = ppBegruendung.value
                    .replace(/[AaOoUu]\u0308/g, umlaut => umlaut.normalize('NFC'))
                    .replace(/\r\n?|[\u0085\u2028\u2029]/g, '\n')
                    .replace(/[\t\p{Zs}]/gu, ' ')
                    .replace(/[^a-zA-ZÀ-ÖØ-öø-žẞ0-9 \n.,:;!?–()/'"„“%&+\-]/g, '');
                if (ppBegruendung.value !== bereinigt)
                    ppBegruendung.value = bereinigt;

                ppBegruendung.required = isPp;
                ppBegruendung.disabled = !isPp;
                ppBegruendungZeichen.textContent = `noch ${Math.max(0, 300 - bereinigt.length)} Zeichen verfügbar`;
                if (isPp)
                    submitButton.disabled = bereinigt.trim().length === 0 || bereinigt.length > 300;
                submitButton.innerHTML = isPp && isPpSchule ? 'Begründung speichern' : 'Speichern und alle<br/>Testgruppen umstellen';
            };
            ppBegruendung?.addEventListener('input', aktualisiereTestmodus);
            ppBegruendung?.addEventListener('blur', () => {
                ppBegruendung.value = ppBegruendung.value.trim();
                aktualisiereTestmodus();
            });

            // Hinweis nur einblenden, wenn bereits ein Testmodus ausgewählt wurde
            if (this.schulstatus.is_cbt_schule_mathematik === null)
                document.querySelector(`#hinweis-testmodus-testheftauswahl`)?.classList.add('hidden');
            else
                document.querySelector(`#hinweis-testmodus-testheftauswahl`)?.classList.remove('hidden');

            // Initialisiert das TomSelect-Dropdown für den Testmodus
            // @ts-ignore
            new TomSelect(`#testmodus-select`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                onChange: aktualisiereTestmodus
            });
            aktualisiereTestmodus();

            // Fügt einen Event-Listener für den Speichern-Button hinzu
            submitButton.addEventListener('click', async () => {
                try {
                    aktualisiereTestmodus();
                    if (submitButton.disabled)
                        return;
                    const modusValue = testmodusSelect.value;
                    const begruendung = modusValue === 'pp' ? ppBegruendung?.value.trim() ?? null : null;

                    // Eine reine Begründungsänderung stellt keine Testgruppen um
                    if (!(begruendung !== null && modusValue === 'pp' && isPpSchule)) {
                        // Erstellt und zeigt ein Bestätigungsmodal an
                        const testmodusModal = new Modal(this);
                        const modalantwort = await testmodusModal.show({
                            title: `Möchten Sie den Testmodus der gesamten Schule wirklich ändern?`,
                            body: `
                                <p>
                                    Wenn Sie den Testmodus der Schule ändern, werden alle Gruppen in diesem Modus (CBT / PP) getestet.<br/>
                                    Dies ist nur möglich, solange in der gesamten Schule noch keine Ergebnisse im Portal hinterlegt wurden. Individuelle Testentscheidungen bleiben erhalten.
                                </p>
                                <p>
                                    Um fortzufahren, geben Sie im Bestätigungsfeld <i>"OK"</i> ein.
                                </p>
                            `,
                            hasSubmit: true,
                            hasCancel: true,
                            hasBestaetigung: true,
                            width: 600,
                            cancelText: 'Abbrechen',
                            submitText: 'Testmodus ändern'
                        });

                        if (modalantwort !== true)
                            return;
                    }

                    // Sendet die Modusänderung an die API
                    const api = VeraApi.getInstance(this);
                    const testmodusAendernResponse = await api.postTestmodus(modusValue, begruendung);

                    // Lädt die Seite bei Erfolg neu
                    if (testmodusAendernResponse === true) {
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    }
                } catch (e) {
                    Utilities.errorToSentry(e);
                }
            });
        }

        if (this.sessionvariablen.projekt.weiche === 'vera5') {
            const mathematik = this.schulstatus.testheft_schwierigkeit_mathematik ?? null;
            const deutsch = this.schulstatus.testheft_schwierigkeit_deutsch ?? null;
            const testheftvarianteGespeichert = mathematik !== null || deutsch !== null;
            const testheftvarianteSelect = document.querySelector(`#testheftvariante-select`) as HTMLSelectElement;
            const submitButton = document.querySelector(`#testheftvariante-speichern`) as HTMLButtonElement;

            if (mathematik === deutsch && (mathematik === 1 || mathematik === 2))
                testheftvarianteSelect.value = mathematik.toString();
            else
                testheftvarianteSelect.value = '-';

            testheftvarianteSelect.disabled = testheftvarianteGespeichert;

            // @ts-ignore
            new TomSelect(`#testheftvariante-select`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                onChange: (value: string) => {
                    submitButton.disabled = testheftvarianteGespeichert || value === '-';
                }
            });

            document.querySelector(`button#testheftvariante-speichern`)
                ?.addEventListener('click', async () => {
                    try {
                        const testheftvarianteModal = new Modal(this);
                        const modalantwort = await testheftvarianteModal.show({
                            title: `Möchten Sie diese Testheftvariante verbindlich festlegen?`,
                            body: `
                                <p>
                                    Die ausgewählte Testheftvariante gilt schulweit für Deutsch und Mathematik und kann anschließend nicht mehr geändert werden.
                                </p>
                                <p>
                                    Um fortzufahren, geben Sie im Bestätigungsfeld <i>"OK"</i> ein.
                                </p>
                            `,
                            hasSubmit: true,
                            hasCancel: true,
                            hasBestaetigung: true,
                            width: 600,
                            cancelText: 'Abbrechen',
                            submitText: 'Testheftvariante<br/>speichern'
                        });

                        if (modalantwort !== true)
                            return;

                        const schwierigkeit = Number(testheftvarianteSelect.value);
                        if ([1, 2].includes(schwierigkeit) === false)
                            return;

                        const api = VeraApi.getInstance(this);
                        const testheftvarianteResponse = await api.postTestheftvariante(schwierigkeit);
                        if (testheftvarianteResponse === true) {
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }
                    } catch (e) {
                        Utilities.errorToSentry(e);
                    }
                });
        }

        return true;
    }

    /**
     * Wird asynchron ausgeführt, nachdem die Seite vollständig geladen und verarbeitet wurde.
     * @returns {Promise<boolean>} Ein Promise, das true zurückgibt.
     */
    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    /**
     * Wird asynchron ausgeführt, bevor die Seite verlassen wird.
     * @returns {Promise<boolean>} Ein Promise, das true zurückgibt.
     */
    override async onSeiteLeave(): Promise<boolean> {
        return true;
    }
}
